import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { itemAtomFamily } from '@/atoms/_editorAtoms'
import {
  caseImagesAtom,
  caseImageVariantPricingAtom,
} from '@/atoms/caseImageAtoms'
import { Icon } from '@/components/priority/Icon/Icon'
import {
  LowResAnchor,
  LowResEnhanceLink,
  LowResIconButton,
  LowResTooltip,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { UPSCALE_TARGET_PPI } from '@messenger/consts'
import { isUpscaleAspectSupported } from '@messenger/utils'
import { useAiImageEditsEnabled, useSharedEnhance } from '@/hooks/caseImages'
import { requiredImagePixelSizeForPpi } from '@/utils/editor/calculateImageQuality'
import { useUpscalePriceLabel } from '@/hooks/caseImages/useUpscalePriceLabel'
import { resolveCaseImageForItem } from '@/utils/editor/resolveCaseImageForItem'
import type { EditedWithAiItem } from '@/utils/editor/editedWithAi'
import { ITEM_LOW_RES_TEST_IDS } from './lowResolutionWarningTestIds'

/**
 * The print quality range the tooltip asks for. The warning itself fires
 * below the product's threshold (see `useCheckImageQuality`); this is the
 * range the design quotes as "desired".
 */
const DESIRED_PPI_RANGE = '200-300'

type LowResolutionWarningProps = {
  /** The canvas item the warning sits on. */
  itemId: string
  /** The item's measured print quality, shown rounded in the copy. */
  effectivePixelsPerInch: number
  /** The pixel size of the file the item shows, as the PPI check measured it. */
  originalImagePixelSize?: { width: number | null; height: number | null }
}

/**
 * The low-resolution warning on a canvas image item: a warning icon whose
 * tooltip states the measured and desired PPI and, when the item shows one
 * of the case's photos, offers "Enhance image size" to open the editor's
 * shared Enhance flow for that photo. Placeholders, logos and design assets
 * have no case photo behind them, so their tooltip carries no action; nor
 * does any item's while AI image edits are off (`useAiImageEditsEnabled`),
 * when the warning still states the measured and desired PPI. Nor does an
 * item showing a render of the photo, a Pintura export (`editedUrl`) or an
 * AI cutout (`aiVariantId`): an accepted enhancement moves the item's `url`,
 * but the render outranks it on the page and is left as it is
 * (`itemsToSwapToEnhancedImage`), so Enhance would bill for a file the item
 * never shows. The canvas photo's enhancement actions skip the same items
 * (`enhancementActionForItem`). Nor does a photo past the 3:1 shape limit
 * the model takes (`isUpscaleAspectSupported`, measured on the file the
 * item shows), which the grid tile hides Enhance for too.
 *
 * The Enhance it starts asks for the size this placement needs to print at
 * `UPSCALE_TARGET_PPI`, worked out from the same measurement the warning
 * shows, so an enhancement the server can make that large clears it.
 *
 * Opening and closing is CSS-driven (hover, focus and a click that pins it)
 * so the tooltip stays while the pointer travels onto the link. The price
 * comes from the pricing atom, requested on first hover when the Case tab
 * has not loaded it yet, and is left out of the link until it arrives.
 */
export const LowResolutionWarning = ({
  itemId,
  effectivePixelsPerInch,
  originalImagePixelSize,
}: LowResolutionWarningProps) => {
  const { t } = useTranslation()
  const tooltipId = useId()
  const item = useRecoilValue(itemAtomFamily(itemId)) as EditedWithAiItem
  const caseImages = useRecoilValue(caseImagesAtom)
  const pricing = useRecoilValue(caseImageVariantPricingAtom)
  const price = useUpscalePriceLabel()
  const shared = useSharedEnhance()
  const aiImageEditsEnabled = useAiImageEditsEnabled()
  const [pinned, setPinned] = useState(false)

  const caseImage = resolveCaseImageForItem(caseImages, item)
  const showsRender = Boolean(
    item?.image?.editedUrl || item?.image?.aiVariantId,
  )
  const width = originalImagePixelSize?.width
  const height = originalImagePixelSize?.height
  const aspectSupported = isUpscaleAspectSupported(
    width && height ? { width, height } : null,
  )
  const canEnhance =
    aiImageEditsEnabled &&
    shared !== null &&
    caseImage !== null &&
    !showsRender &&
    aspectSupported

  const body = t('itemControls.lowResolution.body', {
    currentPpi: Math.round(effectivePixelsPerInch),
    desiredPpi: DESIRED_PPI_RANGE,
  })
  const action = price
    ? t('itemControls.lowResolution.enhanceWithPrice', { price })
    : t('itemControls.lowResolution.enhance')

  const requestPricing = () => {
    if (canEnhance && !pricing) void shared?.ensurePricing()
  }

  return (
    <LowResAnchor
      data-pinned={pinned}
      onMouseEnter={requestPricing}
      onFocus={requestPricing}
    >
      <LowResIconButton
        type="button"
        aria-label={t('toast.printQualityWarningAriaLabel')}
        aria-describedby={tooltipId}
        aria-expanded={pinned}
        data-testid={ITEM_LOW_RES_TEST_IDS.BADGE}
        onClick={(event) => {
          event.stopPropagation()
          setPinned((current) => !current)
        }}
      >
        <Icon
          icon="exclamationTriangle"
          width="24px"
          height="24px"
          fill="orange"
        />
      </LowResIconButton>
      <LowResTooltip
        id={tooltipId}
        role="tooltip"
        $pinned={pinned}
        data-testid={ITEM_LOW_RES_TEST_IDS.TOOLTIP}
      >
        {body}
        {canEnhance && (
          <>
            {' '}
            <LowResEnhanceLink
              type="button"
              data-testid={ITEM_LOW_RES_TEST_IDS.ENHANCE}
              onClick={(event) => {
                event.stopPropagation()
                setPinned(false)
                const targetSize = originalImagePixelSize
                  ? requiredImagePixelSizeForPpi(
                      { effectivePixelsPerInch, originalImagePixelSize },
                      UPSCALE_TARGET_PPI,
                    )
                  : null
                if (targetSize) {
                  void shared.enhanceCaseImage(caseImage.id, { targetSize })
                } else {
                  void shared.enhanceCaseImage(caseImage.id)
                }
              }}
            >
              {action}
            </LowResEnhanceLink>
          </>
        )}
      </LowResTooltip>
    </LowResAnchor>
  )
}
